import { Fragment, type ReactNode } from 'react';
import styles from './MarkdownContent.module.css';

type MarkdownContentProps = {
  content: string;
  className?: string;
  tone?: 'default' | 'briefing';
};

function parseInlineMarkdown(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const regex = /(`[^`\n]+`|\*\*[^*\n]+\*\*)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const token = match[0];
    if (token.startsWith('`')) {
      parts.push(
        <code key={key++} className={styles.inlineCode}>
          {token.slice(1, -1)}
        </code>
      );
    } else {
      parts.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length ? parts : [text];
}

function isUnorderedListLine(line: string): boolean {
  return /^[-*]\s+/.test(line.trim());
}

function isOrderedListLine(line: string): boolean {
  return /^\d+\.\s+/.test(line.trim());
}

function headingLevel(line: string): 1 | 2 | 3 | null {
  const match = line.trim().match(/^(#{1,3})\s+(.+)$/);
  if (!match) return null;
  return match[1].length as 1 | 2 | 3;
}

function headingText(line: string): string {
  return line.trim().replace(/^#{1,3}\s+/, '');
}

function renderHeading(level: 1 | 2 | 3, text: string, key: number): ReactNode {
  const className =
    level === 1 ? styles.heading1 : level === 2 ? styles.heading2 : styles.heading3;
  const Tag = level === 1 ? 'h2' : level === 2 ? 'h3' : 'h4';

  return (
    <Tag key={key} className={className}>
      {parseInlineMarkdown(text)}
    </Tag>
  );
}

function renderParagraphBlock(lines: string[], key: number): ReactNode {
  return (
    <p key={key} className={styles.paragraph}>
      {lines.map((line, lineIndex) => (
        <span key={lineIndex}>
          {lineIndex > 0 ? <br /> : null}
          {parseInlineMarkdown(line)}
        </span>
      ))}
    </p>
  );
}

export function renderMarkdownBlocks(content: string): ReactNode {
  const blocks = content.split(/\n\n+/).filter((block) => block.trim());

  if (blocks.length === 0) {
    return null;
  }

  return blocks.map((block, blockIndex) => {
    const lines = block.split('\n');
    if (lines.length === 0) {
      return null;
    }

    const firstHeading = headingLevel(lines[0]);
    if (firstHeading) {
      const bodyLines = lines.slice(1);
      if (bodyLines.length === 0 || bodyLines.every((line) => !line.trim())) {
        return renderHeading(firstHeading, headingText(lines[0]), blockIndex);
      }

      const trimmedBodyLines = bodyLines.filter((line) => line.trim());
      const bodyIsUnordered = trimmedBodyLines.every(isUnorderedListLine);
      const bodyIsOrdered = trimmedBodyLines.every(isOrderedListLine);

      return (
        <Fragment key={blockIndex}>
          {renderHeading(firstHeading, headingText(lines[0]), blockIndex)}
          {bodyIsUnordered ? (
            <ul className={styles.list}>
              {trimmedBodyLines.map((line, lineIndex) => (
                <li key={lineIndex} className={styles.listItem}>
                  {parseInlineMarkdown(line.trim().replace(/^[-*]\s+/, ''))}
                </li>
              ))}
            </ul>
          ) : bodyIsOrdered ? (
            <ol className={styles.list}>
              {trimmedBodyLines.map((line, lineIndex) => (
                <li key={lineIndex} className={styles.listItem}>
                  {parseInlineMarkdown(line.trim().replace(/^\d+\.\s+/, ''))}
                </li>
              ))}
            </ol>
          ) : (
            renderParagraphBlock(bodyLines, blockIndex + 1000)
          )}
        </Fragment>
      );
    }

    const allUnordered = lines.every(isUnorderedListLine);
    const allOrdered = lines.every(isOrderedListLine);

    if (allUnordered) {
      return (
        <ul key={blockIndex} className={styles.list}>
          {lines.map((line, lineIndex) => (
            <li key={lineIndex} className={styles.listItem}>
              {parseInlineMarkdown(line.trim().replace(/^[-*]\s+/, ''))}
            </li>
          ))}
        </ul>
      );
    }

    if (allOrdered) {
      return (
        <ol key={blockIndex} className={styles.list}>
          {lines.map((line, lineIndex) => (
            <li key={lineIndex} className={styles.listItem}>
              {parseInlineMarkdown(line.trim().replace(/^\d+\.\s+/, ''))}
            </li>
          ))}
        </ol>
      );
    }

    return renderParagraphBlock(lines, blockIndex);
  });
}

export function MarkdownContent({ content, className, tone = 'default' }: MarkdownContentProps) {
  return (
    <div
      className={[
        styles.markdown,
        tone === 'briefing' ? styles.markdownBriefing : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {renderMarkdownBlocks(content)}
    </div>
  );
}
